/**
 * MarqueeTicker
 * @description Web component for creating infinite scrolling marquee/ticker effect
 * @example
<marquee-ticker data-marquee-speed="50">
  <div class="marquee">
    <div class="marquee-track">
      <div class="marquee-slide">
        <p class="marquee-content">Your scrolling text content here</p>
      </div>
    </div>
  </div>
</marquee-ticker>
 */

class MarqueeTicker extends HTMLElement {
  constructor() {
    super();

    // Bind event handlers for proper cleanup
    this.boundHandleResize = this.handleResize.bind(this);
    this.boundHandleMouseEnter = this.handleMouseEnter.bind(this);
    this.boundHandleMouseLeave = this.handleMouseLeave.bind(this);
  }

  connectedCallback() {
    this.marquee = this.querySelector('.marquee');
    this.track = this.querySelector('.marquee-track');
    this.content = this.querySelector('.marquee-content');

    // Stop if required elements are not found
    if (!this.marquee || !this.track || !this.content) {
      return;
    }

    // Default speed (pixels per second)
    this.speed = parseInt(this.dataset.marqueeSpeed) || 50;

    // Default spacing (pixels)
    this.spacing = parseInt(this.dataset.marqueeSpacing) || 24;

    // Animation variables
    this.animationId = null;
    this.isPaused = false;
    this.init();
  }

  init() {
    // Apply dynamic spacing
    this.applySpacing();

    // Clone content for seamless loop
    this.createClones();

    // Set up animation
    this.setupAnimation();

    // Pause on hover
    this.setupHoverControls();

    // Handle resize
    this.setupResizeListener();

    // Handle theme editor changes
    this.handleThemeEditor();
  }

  handleThemeEditor() {
    // Listen for theme editor changes (when in Shopify design mode)
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      window.addEventListener('shopify:section:load', () => {
        this.updateSpacing();
      });

      window.addEventListener('shopify:section:reorder', () => {
        this.updateSpacing();
      });
    }
  }

  updateSpacing() {
    // Re-read spacing from data attribute
    const newSpacing = parseInt(this.dataset.marqueeSpacing) || 24;
    if (newSpacing !== this.spacing) {
      this.spacing = newSpacing;
      this.applySpacing();
      // Recreate clones with new spacing
      this.createClones();
    }
  }

  applySpacing() {
    // Set CSS custom property for spacing on the root element
    this.style.setProperty('--marquee-spacing', `${this.spacing}px`);

    // Also apply directly to all marquee-content elements to ensure immediate update
    const contentElements = this.querySelectorAll('.marquee-content');
    contentElements.forEach((content) => {
      content.style.paddingRight = `${this.spacing}px`;
    });

    // Force reflow to ensure changes are applied
    this.offsetHeight;
  }

  createClones() {
    // Clear existing clones
    const existingClones = this.track.querySelectorAll('.marquee-slide--clone');
    existingClones.forEach((clone) => clone.remove());

    const originalSlides = this.track.querySelectorAll('.marquee-slide:not(.marquee-slide--clone)');
    const containerWidth = this.marquee.offsetWidth;

    // Calculate total width of all original slides (including their natural spacing)
    let totalOriginalWidth = 0;
    originalSlides.forEach((slide) => {
      const rect = slide.getBoundingClientRect();
      totalOriginalWidth += rect.width;
    });

    // Always create enough clones to fill at least 2x the container width for smooth scrolling
    const minWidth = containerWidth * 2;
    const setsNeeded = Math.max(2, Math.ceil(minWidth / totalOriginalWidth));

    // Create clones of all slides
    for (let set = 0; set < setsNeeded; set++) {
      originalSlides.forEach((slide, index) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('marquee-slide--clone');
        clone.setAttribute('aria-hidden', 'true');

        // Apply spacing to cloned content
        const clonedContent = clone.querySelector('.marquee-content');
        if (clonedContent) {
          clonedContent.style.paddingRight = `${this.spacing}px`;
        }

        this.track.appendChild(clone);
      });
    }

    // Store the original set width for seamless reset
    this.slideSetWidth = totalOriginalWidth;

    // Set track width to accommodate all sets
    const totalWidth = totalOriginalWidth * (setsNeeded + 1);
    this.track.style.width = `${totalWidth}px`;
  }

  setupAnimation() {
    // Reset position
    this.currentPosition = 0;

    // Start animation
    this.startAnimation();
  }

  startAnimation() {
    let lastTime = 0;

    const animate = (currentTime) => {
      if (!lastTime) lastTime = currentTime;
      const deltaTime = currentTime - lastTime;

      if (!this.isPaused) {
        // Calculate movement based on speed (pixels per second)
        const movement = (this.speed * deltaTime) / 1000;
        this.currentPosition -= movement;

        // Reset position when we've scrolled a full set width
        if (Math.abs(this.currentPosition) >= this.slideSetWidth) {
          this.currentPosition = 0;
        }

        // Apply transform
        this.track.style.transform = `translateX(${this.currentPosition}px)`;
      }

      lastTime = currentTime;
      this.animationId = requestAnimationFrame(animate);
    };

    this.animationId = requestAnimationFrame(animate);
  }

  setupHoverControls() {
    this.marquee.addEventListener('mouseenter', this.boundHandleMouseEnter);
    this.marquee.addEventListener('mouseleave', this.boundHandleMouseLeave);
  }

  handleMouseEnter() {
    this.isPaused = true;
  }

  handleMouseLeave() {
    this.isPaused = false;
  }

  setupResizeListener() {
    window.addEventListener('resize', this.boundHandleResize);
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.stopAnimation();
      this.createClones();
      this.setupAnimation();
    }, 250);
  }

  stopAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  disconnectedCallback() {
    this.stopAnimation();
    clearTimeout(this.resizeTimeout);
    window.removeEventListener('resize', this.boundHandleResize);
    if (this.marquee) {
      this.marquee.removeEventListener('mouseenter', this.boundHandleMouseEnter);
      this.marquee.removeEventListener('mouseleave', this.boundHandleMouseLeave);
    }
  }
}

window.MarqueeTicker = MarqueeTicker;
customElements.define('marquee-ticker', MarqueeTicker);
